import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Pencil } from "lucide-react";
import { ScoreBadge } from "@school-sales-ai/shared/components";
import type {
  Channel,
  Lead,
  LeadDetail as LeadDetailData,
  LeadStage,
  LlmRole,
  Message,
  ReplyChannel,
  ReplyWindow,
} from "@school-sales-ai/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NotesList } from "@/components/NotesList";
import { NoteInput } from "@/components/NoteInput";
import { AGENT_MATERIAL_DISPLAY, FEATURE_KEYS, mediaKindForMarker } from "@school-sales-ai/shared";
import { useClientFeatures } from "@/hooks/useClientFeatures";
import { LeadCandidatesSection } from "@/components/candidates/LeadCandidatesSection";
import { LeadInterestsSection } from "@/components/interests/LeadInterestsSection";
import { LeadVisitSection } from "@/components/visits/LeadVisitSection";
import { LeadOrdersSection } from "@/components/orders/LeadOrdersSection";
import { LeadQuotesSection } from "@/components/quotes/LeadQuotesSection";
import { LeadDeliveryLocationCard } from "@/components/delivery-zones/LeadDeliveryLocationCard";
import { Avatar } from "@/components/Avatar";
import { StagePill } from "@/components/StagePill";
import { CardLabels } from "@/components/CardLabels";
import { LabelsProvider } from "@/components/labels/LabelsProvider";
import { ChannelIcon } from "@/components/ChannelIcon";
import { sessionDividersByMessageId } from "@/lib/session-dividers";
import { groupByDay } from "@/lib/format-day";
import { senderLabel } from "@/lib/message-sender";
import { MarkUnreadButton } from "@/components/lead/MarkUnreadButton";
import { ReplyComposer } from "@/components/lead/ReplyComposer";
import { StageSheet } from "@/components/StageSheet";
import { StageReasonModal } from "@/components/StageReasonModal";
import { EditLeadModal } from "@/components/EditLeadModal";
import { useCrmStore } from "@/stores/crm.store";
import { useLeadDetail, useStageChange, useToggleBot } from "@/hooks/useLeadDetail";
import { useMarkInboxRead } from "@/hooks/useInbox";
import { useLeadNotes } from "@/hooks/useLeadNotes";
import { useLeadMessages } from "@/hooks/useLeadMessages";
import { useBlockContact } from "@/hooks/useWhatsAppBlacklist";
import { stageLabel } from "@/lib/stage-config";
import { usePipelineStagesQuery } from "@/hooks/usePipelineStages";
import { LinkedLeadsBanner } from "@/components/lead/LinkedLeadsBanner";
import { CHANNEL_DOT, CHANNEL_LABEL, isMessagingChannel } from "@/lib/channel";
import { linkedLeadsOf } from "@/lib/linked-leads";
import { cn } from "@/lib/utils";
import { LeadOptOutBadge } from "@/components/broadcasts/LeadOptOutBadge";
import { AssigneeControl } from "@/components/lead-assignment/AssigneeControl";

// ─── Constants ────────────────────────────────────────────

const HANDOFF_TYPE_LABEL: Record<string, string> = {
  user_requested: "Solicitado por lead",
  ai_initiated: "Iniciado por IA",
};

// Where the "back" control returns to. A lead detail is reachable from both the
// Prospectos list and the Pipeline, so the origin is passed as router `state`
// when navigating in. Falls back to Prospectos on a direct load / refresh.
type BackTarget = { path: string; label: string };

const DEFAULT_BACK_TARGET: BackTarget = {
  path: "/pipeline",
  label: "Pipeline de Prospectos",
};

// The list and the board merged into one page, so every origin resolves there.
// "/leads" is still mapped for links carrying the pre-merge state.
const BACK_TARGETS: Record<string, BackTarget> = {
  "/leads": DEFAULT_BACK_TARGET,
  "/pipeline": DEFAULT_BACK_TARGET,
  // Without this entry a lead opened from the Buzón silently sends the user back
  // to the pipeline — resolveBackTarget falls through to the default.
  "/buzon": { path: "/buzon", label: "Buzón" },
};

// Reads `{ from }` off the router location state without an `as` cast.
// `"from" in state` narrows the unknown state so `state.from` is accessible.
function resolveBackTarget(state: unknown): BackTarget {
  if (typeof state === "object" && state !== null && "from" in state) {
    const from = state.from;
    if (typeof from === "string" && BACK_TARGETS[from]) return BACK_TARGETS[from];
  }
  return DEFAULT_BACK_TARGET;
}

// Known capturedFields keys → user-facing Spanish labels. Unknown keys fall
// back to title-cased raw key — flexible enough for new ConversationScript
// fields that this CRM hasn't been updated to know about.
const CAPTURED_FIELD_LABELS: Record<string, string> = {
  periodo: "Periodo",
  beca: "Beca",
  campaign: "Campaña",
  experiencia: "Experiencia",
  email: "Email",
  edad: "Edad",
  ciudad: "Ciudad",
  escuela_origen: "Escuela de origen",
  grado_actual: "Grado actual",
};

function humanizeCapturedKey(key: string): string {
  return CAPTURED_FIELD_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
}

type ChannelFilter = "all" | "web" | "whatsapp" | "instagram" | "human";

const CHANNEL_FILTERS: { value: ChannelFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "web", label: "Web" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "instagram", label: "Instagram" },
  { value: "human", label: "Humano" },
];

const VISIBLE_ROLES = new Set<string>(["user", "assistant", "human_agent"]);

// ─── Helpers ──────────────────────────────────────────────

function getInitials(name: string | null, fallback: string): string {
  if (!name) return fallback.slice(0, 2).toUpperCase();
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0] ?? "").slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// The exact moment, for the `title` on a bubble's clock. The day divider gives the
// date for a whole run of messages and the clock gives the minute; this is the
// escape hatch for "which day was THIS one, exactly".
function formatFullTimestamp(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "Ahora mismo";
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `Hace ${diffHrs} ${diffHrs === 1 ? "hora" : "horas"}`;
  const diffDays = Math.floor(diffHrs / 24);
  return `Hace ${diffDays} ${diffDays === 1 ? "día" : "días"}`;
}

function applyChannelFilter(messages: Message[], filter: ChannelFilter): Message[] {
  if (filter === "all") return messages;
  if (filter === "web") return messages.filter((m) => m.channel === "web");
  if (filter === "whatsapp") return messages.filter((m) => m.channel === "whatsapp");
  if (filter === "instagram") return messages.filter((m) => m.channel === "instagram");
  return messages.filter((m) => m.role === "human_agent");
}

// ─── Desktop: LeadPageHeader ──────────────────────────────

function LeadPageHeader({
  lead,
  backTarget,
}: {
  lead: LeadDetailData;
  backTarget: BackTarget;
}): React.JSX.Element {
  const initials = getInitials(lead.name, lead.sessionRef);
  const openEditModal = useCrmStore((s) => s.openEditModal);
  const navigate = useNavigate();
  return (
    <div className="shrink-0 flex items-center gap-4 border-b border-ink-100 bg-surface px-6 py-4">
      <Link
        to={backTarget.path}
        className="shrink-0 text-xs text-ink-300 hover:text-ink-700 transition-colors"
      >
        ← {backTarget.label}
      </Link>

      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-lime-pale text-moss text-sm font-semibold select-none">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-base font-semibold text-ink-900 truncate">
            {lead.name ?? lead.sessionRef}
          </p>
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-xs text-ink-300 font-mono truncate shrink-0">
              {lead.sessionRef}
            </p>
            {lead.summaryHeadline && (
              <p
                className="text-xs text-ink-400 italic truncate min-w-0"
                title={lead.summaryHeadline}
              >
                {lead.summaryHeadline}
              </p>
            )}
          </div>
          <div className="mt-1.5">
            <LabelsProvider>
              <CardLabels leadId={lead.id} labels={lead.labels} removable />
            </LabelsProvider>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <AssigneeControl
          leadId={lead.id}
          assignedProfileId={lead.assignedProfileId}
          onLeftScope={() => navigate(backTarget.path)}
        />
        <ScoreBadge score={lead.score} size="sm" showLabel />
        <StagePill stage={lead.stage} />
        <MarkUnreadButton leadId={lead.id} backPath={backTarget.path} />
        <button
          type="button"
          onClick={() => openEditModal(lead.id)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-600 hover:bg-ink-050 transition-colors"
          aria-label="Editar lead"
        >
          <Pencil size={14} />
          Editar
        </button>
      </div>
    </div>
  );
}

// ─── Desktop: ChannelChip ─────────────────────────────────

function ChannelChip({ channel }: { channel: Channel }): React.JSX.Element {
  if (channel === "instagram") {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-pink-50 px-1.5 py-0.5 text-[10px] font-medium text-pink-600">
        📷 IG
      </span>
    );
  }
  if (channel === "whatsapp") {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-[#25D366]/10 px-1.5 py-0.5 text-[10px] font-medium text-[#25D366]">
        📱 WA
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-ink-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-500">
      🖥 Web
    </span>
  );
}

// ─── Voice-note indicator ─────────────────────────────────

// Shown next to a message that originated as a WhatsApp voice note (whether transcribed or left as
// the "[Mensaje de voz]" placeholder), so the team knows the prospect spoke rather than typed.
// A `document` row is any file the agent sent — PDF, image, video, or one row
// for a whole gallery. The kind
// lives in the marker's prefix (`[Imagen: …]`), written by the same shared
// builder the send site uses; anything unrecognised reads as a document.
function SentFileIcon({ content }: { content: string }): React.JSX.Element {
  const kind = mediaKindForMarker(content) ?? "document";
  const { icon, label } = AGENT_MATERIAL_DISPLAY[kind];
  return <span title={label}>{icon}</span>;
}

function VoiceNoteChip(): React.JSX.Element {
  return (
    <span
      title="Nota de voz"
      className="inline-flex items-center gap-0.5 rounded-full bg-lime-pale px-1.5 py-0.5 text-[10px] font-medium text-moss"
    >
      🎤 Voz
    </span>
  );
}

// ─── Message origin indicator ─────────────────────────────

/**
 * Where a human reply was written: the CRM composer, or the school's phone.
 *
 * Only ever rendered for `human_agent`. The prospect and the AI have no origin
 * ambiguity; a person does, because the school's WhatsApp is a shared handset and
 * a reply typed on it reaches us as an authorless coexistence echo. In prod that
 * is not the edge case — 2,214 of 2,219 human messages came in that way, and every
 * one of them read simply "Agente" with no way to tell who wrote it.
 *
 * Keyed on `authorName`, the same field the label above it uses, so the two can
 * never contradict each other by showing a person's name beside "Teléfono".
 */
function OriginChip({ authorName }: { authorName: string | null }): React.JSX.Element {
  const fromCrm = authorName !== null;
  return (
    <span
      title={fromCrm ? "Enviado desde el CRM" : "Escrito en el teléfono de la escuela"}
      // A step quieter than ChannelChip, and without an emoji: the channel chip
      // beside it already carries one, and two in a row reads as noise.
      className="inline-flex items-center rounded-full bg-ink-050 px-1.5 py-0.5 text-[10px] font-medium text-ink-500"
    >
      {fromCrm ? "CRM" : "Teléfono"}
    </span>
  );
}

/**
 * Marks an automatic follow-up («Mensajes de seguimiento»): the agent writing
 * to a customer who had gone quiet, not answering something they said. Without
 * it the timeline shows two agent messages in a row and an advisor cannot tell
 * whether the second one was a reply to a message that is missing.
 */
function ReengagementChip(): React.JSX.Element {
  return (
    <span
      title="El agente le escribió porque el cliente dejó de contestar"
      className="inline-flex items-center rounded-full bg-ink-050 px-1.5 py-0.5 text-[10px] font-medium text-ink-500"
    >
      Seguimiento automático
    </span>
  );
}

/** The customer asked the agent to stop writing to them on its own. */
function ReengagementOptOutChip({ since }: { since: string }): React.JSX.Element {
  const date = new Date(since).toLocaleDateString("es-MX", { day: "numeric", month: "long" });
  return (
    <span
      title={`El cliente pidió el ${date} que el agente no le escriba por su cuenta. El agente sigue respondiendo cuando escribe.`}
      className="inline-flex items-center gap-1 rounded-full bg-ink-050 px-2 py-0.5 text-[10px] font-medium text-ink-500"
    >
      🔕 Sin mensajes de seguimiento
    </span>
  );
}

// ─── Day divider ──────────────────────────────────────────

// Separates the timeline into calendar days. The timeline used to show only
// HH:MM, so a two-month gap between two bubbles looked exactly like a two-minute
// one (lead XZ-418: 58 messages, 2 days, 57 days apart).
function DayDivider({ label }: { label: string }): React.JSX.Element {
  return (
    // `self-stretch` is load-bearing: both scrollers are flex columns whose
    // children align self-start/self-end, so without it the rule collapses to
    // the width of its own text and hugs one side.
    <div className="flex items-center gap-3 self-stretch py-1">
      <div className="h-px flex-1 bg-ink-100" />
      <span className="rounded-full bg-ink-050 px-2 py-0.5 text-[10px] font-medium text-ink-500">
        {label}
      </span>
      <div className="h-px flex-1 bg-ink-100" />
    </div>
  );
}

// ─── Session divider ──────────────────────────────────────

// Separates one visit from the next. Visually louder than the day divider on
// purpose: a day boundary is just time passing, while this one is the point
// where the AGENT stops seeing what came before. An advisor reading a reply that
// greets a known customer from scratch has no other way to know why.
function SessionDivider({ label, hint }: { label: string; hint: string | null }): React.JSX.Element {
  return (
    // `self-stretch` for the same reason DayDivider needs it: the scrollers are
    // flex columns whose children align to one side.
    <div className="flex items-center gap-3 self-stretch py-2">
      <div className="h-px flex-1 bg-lime-deep/30" />
      <span className="rounded-full border border-lime-deep/30 bg-lime-deep/5 px-2 py-0.5 text-[10px] font-medium text-lime-deep">
        {label}
        {hint && <span className="font-normal text-ink-500"> · {hint}</span>}
      </span>
      <div className="h-px flex-1 bg-lime-deep/30" />
    </div>
  );
}

// ─── Desktop: ConversationPanel ───────────────────────────

function ConversationPanel({
  leadId,
  channelOrigin,
  aiPaused,
  onToggleBot,
  replyWindow,
  phone,
  reengagementOptedOutAt,
  instagramUserId,
  instagramReplyWindow,
  defaultReplyChannel,
}: {
  leadId: string;
  channelOrigin: Channel;
  aiPaused: boolean;
  onToggleBot: (v: boolean) => void;
  replyWindow: ReplyWindow | null | undefined;
  phone: string | null;
  reengagementOptedOutAt: string | null;
  instagramUserId: string | null;
  instagramReplyWindow: ReplyWindow | null | undefined;
  defaultReplyChannel: ReplyChannel | null | undefined;
}): React.JSX.Element {
  const { data, isLoading } = useLeadMessages(leadId);
  const bottomRef = useRef<HTMLDivElement>(null);
  const hasScrolled = useRef(false);
  const [filter, setFilter] = useState<ChannelFilter>("all");

  useEffect(() => {
    if (data && !hasScrolled.current) {
      bottomRef.current?.scrollIntoView({ behavior: "instant" });
      hasScrolled.current = true;
    }
  }, [data]);

  const allMessages = (data?.messages ?? []).filter((m) => VISIBLE_ROLES.has(m.role));
  const messages = applyChannelFilter(allMessages, filter);
  // Computed over the FILTERED list, like groupByDay and for the same reason: a
  // divider resolved against hidden messages marks the wrong bubble.
  const sessionDividers = sessionDividersByMessageId(messages, data?.sessions ?? []);

  return (
    <div className="flex flex-col flex-1 min-w-0 min-h-0">
      <div className="shrink-0 flex items-center gap-3 border-b border-ink-100 px-6 py-3">
        <span className="text-sm font-semibold text-ink-900">
          Timeline de conversación
        </span>
        {(isMessagingChannel(channelOrigin) || instagramUserId !== null) && (
          aiPaused ? (
            <button
              onClick={() => onToggleBot(false)}
              className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors cursor-pointer"
            >
              ⏸ Agente IA en pausa — control humano
            </button>
          ) : (
            <button
              onClick={() => onToggleBot(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-ink-100 bg-ink-050 px-3 py-1 text-xs font-medium text-ink-500 hover:bg-ink-100 transition-colors cursor-pointer"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-wa-green" />
              Agente IA activo
            </button>
          )
        )}
        {reengagementOptedOutAt && <ReengagementOptOutChip since={reengagementOptedOutAt} />}
        <div className="ml-auto flex items-center gap-1">
          {CHANNEL_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={cn(
                "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                filter === f.value
                  ? "bg-ink-900 text-white"
                  : "bg-ink-050 text-ink-500 hover:bg-ink-100"
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 flex flex-col gap-4">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm text-ink-300">Cargando…</span>
          </div>
        )}
        {!isLoading && allMessages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm text-ink-300">Sin mensajes aún.</span>
          </div>
        )}
        {!isLoading && allMessages.length > 0 && messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm text-ink-300">Sin mensajes con este filtro.</span>
          </div>
        )}
        {/* Grouped over `messages`, the ALREADY-FILTERED list: grouping upstream of
            the channel filter would leave a divider standing over a day whose
            messages are all hidden. */}
        {groupByDay(messages).map((group) => (
          <Fragment key={group.key}>
            <DayDivider label={group.label} />
            {group.items.map((msg) => {
              const divider = sessionDividers.get(msg.id);
              const isUser = msg.role === "user";
              // A CRM-sent reply names the advisor who wrote it. Everything else —
              // including a reply typed on the school's phone, which arrives as an
              // authorless echo — falls back to the role label.
              const label =
                msg.role === "human_agent" && msg.authorName
                  ? msg.authorName
                  : senderLabel(msg.role as LlmRole);
              const time = formatTime(msg.createdAt);
              return (
                <Fragment key={msg.id}>
                {divider && <SessionDivider label={divider.label} hint={divider.hint} />}
                <div
                  className={cn(
                    "flex flex-col gap-1 max-w-[75%]",
                    isUser ? "self-end items-end" : "self-start items-start"
                  )}
                >
                  <div className="flex items-center gap-1.5 px-1">
                    <span className="text-xs text-ink-400">{label}</span>
                    <ChannelChip channel={msg.channel} />
                    {msg.role === "human_agent" && <OriginChip authorName={msg.authorName} />}
                    {msg.isReengagement && <ReengagementChip />}
                    {msg.messageType === "voice_note" && <VoiceNoteChip />}
                    {msg.messageType === "document" && <SentFileIcon content={msg.content} />}
                  </div>
                  {/* The clock lives INSIDE the bubble, the way messaging apps put
                      it. Up in the header row it sat between the sender and up to
                      three chips and got lost among them.
                      `flex-wrap` + `ml-auto` on the clock is what makes it share
                      the line of a one-liner but drop to its own right-aligned
                      row under a wrapped message. Without the wrap it claimed a
                      column of its own and left the text narrowed for the whole
                      height of the bubble. */}
                  <div
                    className={cn(
                      "flex flex-wrap items-end gap-x-2 rounded-xl px-3 py-2 text-sm leading-relaxed",
                      isUser
                        ? "bg-ink-100 text-ink-900"
                        : msg.role === "human_agent"
                          ? "bg-blue-50 border border-blue-100 text-ink-900"
                          : "bg-surface border border-ink-100 text-ink-900"
                    )}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      className="prose prose-sm min-w-0 max-w-none prose-p:my-0.5 prose-ul:my-0.5 prose-li:my-0"
                    >
                      {msg.content}
                    </ReactMarkdown>
                    <span
                      title={formatFullTimestamp(msg.createdAt)}
                      className="ml-auto shrink-0 pb-0.5 text-[10px] leading-none text-ink-300"
                    >
                      {time}
                    </span>
                  </div>
                </div>
                </Fragment>
              );
            })}
          </Fragment>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Composer sits outside the scroller as a shrink-0 footer, mirroring how
          NoteInput anchors the left rail. WhatsApp and Instagram only — a web
          lead has no channel to reply on from here. */}
      {(channelOrigin === "whatsapp" || instagramUserId !== null) && (
        <div className="shrink-0 border-t border-ink-100 bg-surface px-6 py-3">
          <ReplyComposer
            leadId={leadId}
            replyWindow={replyWindow}
            phone={phone}
            aiPaused={aiPaused}
            whatsappAvailable={channelOrigin === "whatsapp"}
            instagram={instagramUserId !== null ? { window: instagramReplyWindow } : null}
            defaultChannel={defaultReplyChannel ?? null}
          />
        </div>
      )}
    </div>
  );
}

// ─── Desktop: ProfileRow ──────────────────────────────────

function ProfileRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-xs text-ink-400">{label}</span>
      <span className="text-right text-xs font-medium text-ink-900">{children}</span>
    </div>
  );
}

// Shows everything the agent extracted during the conversation:
// resolved catalog joins (productName, cycle) plus any free-form
// capturedFields. Hidden when there's nothing to show.
function CapturedDataSection({ lead }: { lead: LeadDetailData }): React.JSX.Element | null {
  const cycleDisplay = lead.cycleLabel ?? lead.cycleKey;
  // Every key the API sends is a real captured field — legacy keys that
  // duplicate a canonical column are stripped server-side in `toLeadDetail`.
  const extraFields = lead.capturedFields
    ? Object.entries(lead.capturedFields).filter(
        ([, v]) => v !== null && v !== undefined && String(v).trim() !== "",
      )
    : [];

  // productName deliberately absent: the programs of interest are their own
  // section now, so a lead whose ONLY captured datum is a program must not make
  // this card render with nothing in it.
  if (!cycleDisplay && extraFields.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
        Datos capturados
      </p>
      <div className="flex flex-col gap-2">
        {cycleDisplay && <ProfileRow label="Ciclo">{cycleDisplay}</ProfileRow>}
        {extraFields.map(([k, v]) => (
          <ProfileRow key={k} label={humanizeCapturedKey(k)}>
            {String(v)}
          </ProfileRow>
        ))}
      </div>
    </div>
  );
}

// ─── Mobile sub-components ────────────────────────────────

type MobileAiStatusProps = {
  lead: Lead;
  onToggle: (v: boolean) => void;
};

function MobileAiStatus({ lead, onToggle }: MobileAiStatusProps): React.JSX.Element {
  const isWa = isMessagingChannel(lead.channelOrigin) || lead.instagramUserId !== null;
  const bg = lead.aiPaused
    ? "oklch(0.93 0.04 240)"
    : lead.humanRequested
      ? "oklch(0.95 0.06 65)"
      : "oklch(0.93 0.05 155)";
  const color = lead.aiPaused
    ? "oklch(0.30 0.10 240)"
    : lead.humanRequested
      ? "oklch(0.38 0.12 65)"
      : "oklch(0.28 0.10 155)";
  const label = lead.aiPaused
    ? "⏸ Control humano"
    : lead.humanRequested
      ? "🙋 Humano solicitado"
      : "✨ Agente IA activo";

  const handoffLabel = lead.handoffType
    ? HANDOFF_TYPE_LABEL[lead.handoffType] ?? lead.handoffType
    : null;

  return (
    <div className="flex flex-col items-center gap-1">
      {isWa ? (
        <button
          type="button"
          onClick={() => onToggle(!lead.aiPaused)}
          className="px-3 py-[5px] rounded-full text-[11px] font-semibold cursor-pointer"
          style={{ background: bg, color }}
        >
          {label}
        </button>
      ) : (
        <span
          className="px-3 py-[5px] rounded-full text-[11px] font-semibold"
          style={{ background: bg, color }}
        >
          {label}
        </span>
      )}
      {handoffLabel && (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
          🤝 {handoffLabel}
        </span>
      )}
      {lead.reengagementOptedOutAt && <ReengagementOptOutChip since={lead.reengagementOptedOutAt} />}
    </div>
  );
}

function MobileCapturedDataCard({ lead }: { lead: LeadDetailData }): React.JSX.Element | null {
  const cycleDisplay = lead.cycleLabel ?? lead.cycleKey;
  // Every key the API sends is a real captured field — legacy keys that
  // duplicate a canonical column are stripped server-side in `toLeadDetail`.
  const extraFields = lead.capturedFields
    ? Object.entries(lead.capturedFields).filter(
        ([, v]) => v !== null && v !== undefined && String(v).trim() !== "",
      )
    : [];

  // productName deliberately absent: the programs of interest are their own
  // section now, so a lead whose ONLY captured datum is a program must not make
  // this card render with nothing in it.
  if (!cycleDisplay && extraFields.length === 0) return null;

  const rows: Array<[string, string]> = [
    ...(cycleDisplay ? [["Ciclo", cycleDisplay] as [string, string]] : []),
    ...extraFields.map(([k, v]) => [humanizeCapturedKey(k), String(v)] as [string, string]),
  ];

  return (
    <div className="bg-white rounded-lg border border-ink-100 overflow-hidden">
      <div className="px-3.5 py-3 border-b border-ink-050 text-[11px] font-bold text-ink-400 uppercase tracking-wider">
        Datos capturados
      </div>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between items-center px-3.5 py-3 border-b border-ink-050 gap-3 last:border-b-0">
          <span className="text-[13px] text-ink-500 shrink-0">{k}</span>
          <span className="text-[13px] font-medium text-ink-900 text-right">{v}</span>
        </div>
      ))}
    </div>
  );
}

function MobileTimelineMessage({ msg }: { msg: Message }): React.JSX.Element {
  const isUser = msg.role === "user";
  const isHuman = msg.role === "human_agent";
  const bg = isUser
    ? "white"
    : msg.channel === "whatsapp"
      ? "oklch(0.93 0.04 145)"
      : isHuman
        ? "oklch(0.93 0.04 240)"
        : "var(--ink-050, oklch(0.97 0.002 90))";
  const borderColor = isUser
    ? "oklch(0.93 0.003 90)"
    : msg.channel === "whatsapp"
      ? "oklch(0.84 0.06 145)"
      : isHuman
        ? "oklch(0.84 0.06 240)"
        : "oklch(0.93 0.003 90)";
  const senderColor = isHuman
    ? "oklch(0.35 0.10 240)"
    : isUser
      ? "oklch(0.50 0.005 90)"
      : msg.channel === "whatsapp"
        ? "oklch(0.35 0.10 145)"
        : "oklch(0.50 0.005 90)";
  // Through senderLabel, like the desktop panel: this used to hardcode its own
  // strings ("🤖 WA" / "👤 Agente") and ignore authorName, so the same message
  // read differently — and namelessly — depending on the device you opened it on.
  const sender = isHuman && msg.authorName ? msg.authorName : senderLabel(msg.role as LlmRole);

  return (
    <div className={`flex flex-col gap-[3px] ${isUser ? "items-end" : "items-start"}`}>
      <div className="flex items-center gap-1 text-[10px] text-ink-400">
        <span className="font-medium" style={{ color: senderColor }}>{sender}</span>
        <ChannelIcon channel={msg.channel} />
        {isHuman && <OriginChip authorName={msg.authorName} />}
        {msg.isReengagement && <ReengagementChip />}
        {msg.messageType === "voice_note" && <span title="Nota de voz">🎤</span>}
        {msg.messageType === "document" && <SentFileIcon content={msg.content} />}
      </div>
      {/* Same move as the desktop bubble: the clock sits bottom-right inside it,
          not in the header row. */}
      <div
        className={`flex max-w-[82%] flex-wrap items-end gap-x-2 px-3 py-[9px] text-[13px] leading-relaxed text-ink-900 ${
          isUser ? "rounded-[12px_3px_12px_12px]" : "rounded-[3px_12px_12px_12px]"
        }`}
        style={{ background: bg, border: `1px solid ${borderColor}` }}
      >
        <span className="min-w-0 whitespace-pre-wrap">{msg.content}</span>
        <span
          title={formatFullTimestamp(msg.createdAt)}
          className="ml-auto shrink-0 pb-px text-[10px] leading-none text-ink-400"
        >
          {formatTime(msg.createdAt)}
        </span>
      </div>
    </div>
  );
}

// ─── LeadDetail ───────────────────────────────────────────

// Blocks a WhatsApp contact by its current identifier (phone, or the raw `@lid` digits when
// WhatsApp never shared the real number). Two-step confirm — no browser dialog. The lead's
// `phone` is exactly what inbound routing matches on, so blocking it stops future AI replies.
function BlockContactButton({ identifier }: { identifier: string }): React.JSX.Element {
  const { mutate: block, isPending } = useBlockContact();
  const [confirming, setConfirming] = useState(false);

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        if (!confirming) {
          setConfirming(true);
          return;
        }
        block(identifier, { onSettled: () => setConfirming(false) });
      }}
      onBlur={() => setConfirming(false)}
      className={cn(
        "w-full rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors disabled:opacity-50",
        confirming
          ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
          : "border-ink-200 text-ink-600 hover:bg-ink-050",
      )}
    >
      {isPending
        ? "Bloqueando…"
        : confirming
          ? "¿Confirmar? El bot ignorará a este contacto"
          : "Bloquear contacto"}
    </button>
  );
}

export function LeadDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const leadId = id ?? "";
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget = resolveBackTarget(location.state);

  const { data: lead, isLoading: leadLoading } = useLeadDetail(leadId);
  const { data: notesData } = useLeadNotes(leadId);
  const { data: messagesData, isLoading: messagesLoading } = useLeadMessages(leadId);
  const {
    requestStage,
    pendingStage,
    confirmPending,
    cancelPending,
    isPending: stagePending,
  } = useStageChange(leadId, lead?.stage);
  const { data: stages } = usePipelineStagesQuery();
  const { data: features } = useClientFeatures();
  // Who the lead is enquiring FOR is the enrollment capability. The endpoints
  // 403 without it too — this only keeps the section off a screen that could not
  // write to it anyway.
  const canEnrollment = (features ?? []).includes(FEATURE_KEYS.enrollment);
  const canOrders = (features ?? []).includes(FEATURE_KEYS.orders);
  const canQuotes = (features ?? []).includes(FEATURE_KEYS.quotes);
  // Where a customer's orders go only exists for a shop that delivers.
  const canDelivery = (features ?? []).includes(FEATURE_KEYS.delivery);
  const { mutate: toggleBot } = useToggleBot(leadId);
  const { mutate: markInboxRead } = useMarkInboxRead();
  const openEditModal = useCrmStore((s) => s.openEditModal);

  // Dismiss this lead in the Buzón, once per lead. Team-wide, not per user.
  //
  // The ref guard is the point: useLeadMessages polls every 10s, and keying this
  // effect on anything that settles with the poll would re-fire the mutation
  // forever. It must fire on the lead, and only on the lead.
  const markedReadFor = useRef<string | null>(null);
  useEffect(() => {
    if (!leadId || markedReadFor.current === leadId) return;
    markedReadFor.current = leadId;
    markInboxRead(leadId);
  }, [leadId, markInboxRead]);

  const notes = notesData?.notes ?? [];
  const allMessages = (messagesData?.messages ?? []).filter((m) => VISIBLE_ROLES.has(m.role));
  // Mobile has no channel filter, so the rendered list IS the visible list.
  const sessionDividers = sessionDividersByMessageId(allMessages, messagesData?.sessions ?? []);

  // Mobile state
  const [tab, setTab] = useState<"timeline" | "info" | "notes">("timeline");
  const [showStageSheet, setShowStageSheet] = useState(false);

  const mobileBottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tab === "timeline" && !messagesLoading && allMessages.length > 0) {
      setTimeout(() => mobileBottomRef.current?.scrollIntoView({ behavior: "instant" }), 50);
    }
  }, [tab, messagesLoading, allMessages.length]);

  if (leadLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-sm text-ink-300">Cargando…</span>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-sm text-red-500">Prospecto no encontrado.</span>
      </div>
    );
  }

  return (
    <>
      {/* ── Mobile layout ─────────────────────────── */}
      {/* `flex-1 min-h-0` rather than `h-full`: height:100% has to resolve against
          <main>, which is itself a flex item with no height property of its own, and
          that resolution is the fragile link in the chain. Filling the flex line
          needs no percentage to resolve, and `min-h-0` lets the tab below actually
          shrink to the space available instead of growing to its content and
          overflowing the viewport. */}
      <div className="md:hidden flex flex-1 min-h-0 flex-col relative animate-slide-in-right">

        {/* Detail header */}
        <div className="bg-white border-b border-ink-100 shrink-0">
          <div className="flex items-center gap-2.5 px-4 py-2.5">
            <button
              type="button"
              onClick={() => navigate(backTarget.path)}
              aria-label={`Volver a ${backTarget.label}`}
              className="text-[22px] text-ink-500 leading-none p-1 shrink-0 cursor-pointer"
            >
              ‹
            </button>
            <Avatar name={lead.name ?? lead.sessionRef} size={36} />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[18px] text-ink-900 leading-none truncate">
                {lead.name ?? lead.sessionRef}
              </div>
              <div className="flex items-center gap-1.5 min-w-0 mt-0.5">
                <div className="text-[11px] text-ink-500 font-mono shrink-0">
                  {lead.sessionRef}
                </div>
                {lead.summaryHeadline && (
                  <div
                    className="text-[11px] text-ink-400 italic truncate min-w-0"
                    title={lead.summaryHeadline}
                  >
                    {lead.summaryHeadline}
                  </div>
                )}
              </div>
            </div>
            <ScoreBadge score={lead.score} size="lg" showLabel />
            <MarkUnreadButton leadId={lead.id} backPath={backTarget.path} compact />
            <button
              type="button"
              onClick={() => openEditModal(lead.id)}
              className="p-2 rounded-lg text-ink-400 hover:text-ink-700 hover:bg-ink-050 transition-colors shrink-0"
              aria-label="Editar lead"
            >
              <Pencil size={18} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-ink-100">
            {(["timeline", "info", "notes"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`flex-1 py-2.5 text-xs border-b-2 transition-all cursor-pointer ${
                  tab === t
                    ? "font-semibold text-moss border-moss"
                    : "font-normal text-ink-500 border-transparent"
                }`}
              >
                {t === "timeline" ? "Timeline" : t === "info" ? "Perfil" : `Notas (${notes.length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Timeline tab */}
        {tab === "timeline" && (
          // Split into a scroller plus a shrink-0 footer so the composer is
          // pinned instead of scrolling away with the messages — the same shape
          // the Notas tab already uses for NoteInput.
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-3.5 py-3 flex flex-col gap-2 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:bg-ink-300 [&::-webkit-scrollbar-thumb]:rounded-full">
              <LinkedLeadsBanner lead={lead} />
              <MobileAiStatus lead={lead} onToggle={(v) => toggleBot(v)} />
              {messagesLoading && (
                <div className="flex justify-center py-8">
                  <span className="text-sm text-ink-300">Cargando…</span>
                </div>
              )}
              {/* No channel filter on mobile, so the full visible list is what
                  gets grouped. */}
              {groupByDay(allMessages).map((group) => (
                <Fragment key={group.key}>
                  <DayDivider label={group.label} />
                  {group.items.map((msg) => {
                    const divider = sessionDividers.get(msg.id);
                    return (
                      <Fragment key={msg.id}>
                        {divider && (
                          <SessionDivider label={divider.label} hint={divider.hint} />
                        )}
                        <MobileTimelineMessage msg={msg} />
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
              <div ref={mobileBottomRef} />
            </div>
            {(lead.channelOrigin === "whatsapp" || lead.instagramUserId !== null) && (
              <div className="shrink-0 border-t border-ink-100 bg-white px-4 py-2.5">
                <ReplyComposer
                  leadId={leadId}
                  replyWindow={lead.replyWindow}
                  phone={lead.phone}
                  aiPaused={lead.aiPaused}
                  whatsappAvailable={lead.channelOrigin === "whatsapp"}
                  instagram={lead.instagramUserId !== null ? { window: lead.instagramReplyWindow } : null}
                  defaultChannel={lead.defaultReplyChannel}
                />
              </div>
            )}
          </div>
        )}

        {/* Perfil tab */}
        {tab === "info" && (
          <div className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-3">
            <AssigneeControl
              leadId={lead.id}
              assignedProfileId={lead.assignedProfileId}
              onLeftScope={() => navigate(backTarget.path)}
              className="self-start max-w-full bg-white"
            />
            {/* Profile card */}
            <div className="bg-white rounded-lg border border-ink-100 overflow-hidden">
              <div className="px-3.5 py-3 border-b border-ink-050 text-[11px] font-bold text-ink-400 uppercase tracking-wider">
                Perfil
              </div>
              {[
                ["Teléfono", lead.phone ?? "—"],
                ["Canal", CHANNEL_LABEL[lead.channelOrigin]],
                ...(lead.instagramUsername ? [["Instagram", `@${lead.instagramUsername}`] as const] : []),
                ["Sesión", lead.sessionRef],
                ...(lead.entryPoint ? [["Origen", lead.entryPoint]] : []),
                ...(lead.email ? [["Email", lead.email]] : []),
                ["Última actividad", formatRelativeTime(lead.lastInteractionAt)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between items-center px-3.5 py-3 border-b border-ink-050 gap-3 last:border-b-0">
                  <span className="text-[13px] text-ink-500 shrink-0">{k}</span>
                  <span className="text-[13px] font-medium text-ink-900 text-right">{v}</span>
                </div>
              ))}
            </div>

            {/* Opt-out from BULK sends. Deliberately next to — and distinct
                from — Bloquear: the blacklist stops the AI from answering at
                all, this only removes them from campaigns. Someone who replied
                BAJA keeps talking to the school. */}
            {lead.phone ? (
              <div className="px-1">
                <LeadOptOutBadge leadId={lead.id} hasPhone={!!lead.phone} />
              </div>
            ) : null}

            {/* Bloquear — WhatsApp leads only */}
            {lead.channelOrigin === "whatsapp" && lead.phone && (
              <BlockContactButton identifier={lead.phone} />
            )}

            {/* Captured data card — joined catalog fields + free-form capturedFields */}
            <MobileCapturedDataCard lead={lead} />

            {canEnrollment && <LeadCandidatesSection lead={lead} />}
            <LeadInterestsSection lead={lead} />

            {/* Order history; self-hides when the lead never bought */}
            {canDelivery && <LeadDeliveryLocationCard leadId={lead.id} />}
            {canOrders && <LeadOrdersSection leadId={lead.id} />}
            {canQuotes && <LeadQuotesSection leadId={lead.id} />}

            {/* Visit card + events; self-hides when there's nothing */}
            <LeadVisitSection leadId={lead.id} />

            {/* Stage card */}
            <div className="bg-white rounded-lg border border-ink-100 overflow-hidden">
              <div className="px-3.5 py-3 border-b border-ink-050 flex items-center justify-between">
                <span className="text-[11px] font-bold text-ink-400 uppercase tracking-wider">Etapa actual</span>
                <StagePill stage={lead.stage} />
              </div>
              <button
                type="button"
                onClick={() => setShowStageSheet(true)}
                className="flex items-center justify-between px-3.5 py-3.5 w-full border-none bg-transparent cursor-pointer text-moss text-[13px] font-medium"
              >
                Cambiar etapa
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Notas tab */}
        {tab === "notes" && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <NotesList notes={notes} />
            </div>
            <div className="shrink-0 border-t border-ink-100 bg-white px-4 py-2.5">
              <NoteInput leadId={leadId} />
            </div>
          </div>
        )}

        {/* Stage bottom sheet */}
        {showStageSheet && (
          <StageSheet
            current={lead.stage}
            onSelect={(stage) => {
              requestStage(stage);
              setShowStageSheet(false);
            }}
            onClose={() => setShowStageSheet(false)}
          />
        )}
      </div>

      {/* ── Desktop layout ────────────────────────── */}
      <div className="hidden md:flex flex-col h-full overflow-hidden">
        <LeadPageHeader lead={lead} backTarget={backTarget} />
        {(linkedLeadsOf(lead).length > 0 || lead.linkedTo) && (
          <div className="shrink-0 border-b border-ink-100 px-6 py-3">
            <LinkedLeadsBanner lead={lead} />
          </div>
        )}

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left panel */}
          <div className="flex w-[320px] shrink-0 flex-col border-r border-ink-100">
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 flex flex-col gap-6">

              {/* PERFIL */}
              <div className="flex flex-col gap-3">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
                  Perfil
                </p>
                <div className="flex flex-col gap-2">
                  <ProfileRow label="Teléfono">{lead.phone ?? "—"}</ProfileRow>
                  <ProfileRow label="Canal">
                    <span className="flex items-center justify-end gap-1.5">
                      <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          CHANNEL_DOT[lead.channelOrigin]
                        )}
                      />
                      {CHANNEL_LABEL[lead.channelOrigin]}
                    </span>
                  </ProfileRow>
                  {lead.instagramUsername && (
                    <ProfileRow label="Instagram">@{lead.instagramUsername}</ProfileRow>
                  )}
                  <ProfileRow label="Sesión">
                    <span className="font-mono">{lead.sessionRef}</span>
                  </ProfileRow>
                  {/* Which CTA on the school's site opened the chat. Untrusted text
                      from the host page — React escapes it here; never move it into
                      raw HTML (an email body) without escaping. */}
                  {lead.entryPoint && (
                    <ProfileRow label="Origen">{lead.entryPoint}</ProfileRow>
                  )}
                  {lead.handoffType && (
                    <ProfileRow label="Handoff">
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                        🤝 {HANDOFF_TYPE_LABEL[lead.handoffType] ?? lead.handoffType}
                      </span>
                    </ProfileRow>
                  )}
                  {lead.email && <ProfileRow label="Email">{lead.email}</ProfileRow>}
                  <ProfileRow label="Última actividad">
                    {formatRelativeTime(lead.lastInteractionAt)}
                  </ProfileRow>
                </div>
              </div>

              {/* DATOS CAPTURADOS — joined catalog fields + free-form capturedFields */}
              <CapturedDataSection lead={lead} />

              {/* PROGRAMAS / PRODUCTOS DE INTERÉS — 1:N, primary marked and swappable */}
              {canEnrollment && <LeadCandidatesSection lead={lead} />}
              <LeadInterestsSection lead={lead} />

              {/* HISTORIAL DE PEDIDOS — self-hides when the lead never bought */}
              {canDelivery && <LeadDeliveryLocationCard leadId={lead.id} />}
              {canOrders && <LeadOrdersSection leadId={lead.id} />}
              {canQuotes && <LeadQuotesSection leadId={lead.id} />}

              {/* VISITA — card + events; self-hides when there's nothing */}
              <LeadVisitSection leadId={lead.id} />

              {/* ETAPA */}
              <div className="flex flex-col gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
                  Etapa
                </p>
                <Select
                  value={lead.stage}
                  onValueChange={(v) => requestStage(v as LeadStage)}
                  disabled={stagePending}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue>{stageLabel(lead.stage, stages)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {(stages ?? []).map((o) => (
                      <SelectItem key={o.key} value={o.key}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Opt-out from BULK sends — distinct from Bloquear below: that
                  one stops the AI answering at all, this only removes them from
                  campaigns. */}
              {lead.phone ? (
                <LeadOptOutBadge leadId={lead.id} hasPhone={!!lead.phone} />
              ) : null}

              {/* BLOQUEAR — WhatsApp leads only */}
              {lead.channelOrigin === "whatsapp" && lead.phone && (
                <BlockContactButton identifier={lead.phone} />
              )}

              {/* NOTAS */}
              <div className="flex flex-col gap-3">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
                  Notas
                </p>
                <NotesList notes={notes} />
              </div>
            </div>

            {/* NoteInput pinned at the bottom */}
            <div className="shrink-0 border-t border-ink-100 px-5 py-4">
              <NoteInput leadId={leadId} />
            </div>
          </div>

          {/* Right panel — conversation timeline */}
          <ConversationPanel
            leadId={leadId}
            channelOrigin={lead.channelOrigin}
            aiPaused={lead.aiPaused}
            onToggleBot={(v) => toggleBot(v)}
            replyWindow={lead.replyWindow}
            phone={lead.phone}
            reengagementOptedOutAt={lead.reengagementOptedOutAt ?? null}
            instagramUserId={lead.instagramUserId}
            instagramReplyWindow={lead.instagramReplyWindow}
            defaultReplyChannel={lead.defaultReplyChannel}
          />
        </div>
      </div>

      <EditLeadModal />

      {pendingStage && (
        <StageReasonModal
          stage={pendingStage}
          leadId={leadId}
          open
          onConfirm={confirmPending}
          onCancel={cancelPending}
        />
      )}
    </>
  );
}
